import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {RoomActivity, RoomEquipment, meetingRoomEntity} from '../entities';
import {AppThunkApiConfig} from '../../createStore';
import {mapiqApi} from '@lib/infrastructure';
import {
  NormalizedMeetingRoomSuggestionsResult,
  MeetingRoomFilters,
  AllMeetingRoomFilters,
  MeetingRoomAmenitiesByBuilding,
} from './types';
import {normalize} from 'normalizr';
import {getCalendarDomainPreference, isHybridMeetingsFeatureEnabled} from '../../data/meetings/helpers';
import {withPayloadType} from '@lib/utils';
import {isOccupancyEnabled} from '../../infrastructure/features/helpers';

const initialState: NormalizedMeetingRoomSuggestionsResult = {meetingRooms: {}, suggestionData: {}};

export const loadAvailableMeetingRoomsWithCapacityFilter = createAsyncThunk<
  NormalizedMeetingRoomSuggestionsResult,
  MeetingRoomFilters,
  AppThunkApiConfig
>(
  'meetingRooms/loadAvailableMeetingRoomsWithCapacityFilter',
  async ({inviteesCount, buildingId, endDateTime, startDateTime}, {extra, getState}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const state = getState();

    if (!isHybridMeetingsFeatureEnabled(state)) {
      return initialState;
    }
    const calendarDomain = getCalendarDomainPreference(state);

    const apiResult = await api.getAvailableMeetingRooms(
      buildingId,
      startDateTime,
      endDateTime,
      calendarDomain,
      inviteesCount,
    );
    const {entities} = normalize<any, NormalizedMeetingRoomSuggestionsResult>(apiResult, [meetingRoomEntity]);
    return entities;
  },
);

export const loadAvailableMeetingRooms = createAsyncThunk<
  NormalizedMeetingRoomSuggestionsResult,
  MeetingRoomFilters,
  AppThunkApiConfig
>(
  'meetingRooms/loadAvailableMeetingRooms',
  async ({buildingId, endDateTime, startDateTime}: MeetingRoomFilters, {extra, getState}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const state = getState();

    if (!isHybridMeetingsFeatureEnabled(state)) {
      return initialState;
    }
    const calendarDomain = getCalendarDomainPreference(state);

    const apiResult = await api.getAvailableMeetingRooms(buildingId, startDateTime, endDateTime, calendarDomain);

    const {entities} = normalize<any, NormalizedMeetingRoomSuggestionsResult>(apiResult, [meetingRoomEntity]);
    return entities;
  },
);

export const loadAllMeetingRooms = createAsyncThunk<
  NormalizedMeetingRoomSuggestionsResult,
  AllMeetingRoomFilters,
  AppThunkApiConfig
>('meetingRooms/loadAllMeetingRooms', async (data, {extra: {apiClient}, getState}) => {
  const state = getState();

  if (!isHybridMeetingsFeatureEnabled(state) && !isOccupancyEnabled(state.data.features.enabledFeatures)) {
    return initialState;
  }
  const calendarDomain = getCalendarDomainPreference(state);

  const api = mapiqApi(apiClient);
  const apiResult = await api.postAllMeetingRooms({...data, calendarDomain});

  const {entities} = normalize<any, NormalizedMeetingRoomSuggestionsResult>(apiResult, [meetingRoomEntity]);

  return entities;
});

export const markAsBookedLocally = createAction(
  'meetingRooms/markAsBookedLocally',
  withPayloadType<{buildingId: string; roomId: string}>(),
);

export const loadBuildingMeetingRoomAmenities = createAsyncThunk<
  MeetingRoomAmenitiesByBuilding,
  void,
  AppThunkApiConfig
>('meetingRooms/loadBuildingMeetingRoomAmenities', async (_, {extra: {apiClient}}) => {
  const api = mapiqApi(apiClient);
  const apiResult = await api.getBuildingMeetingRoomAmenities();

  return Object.keys(apiResult).reduce((acc, curr) => {
    return {
      ...acc,
      [curr]: {
        activities: (apiResult[curr]?.activities ?? []) as RoomActivity[],
        equipment: (apiResult[curr]?.equipment ?? []) as RoomEquipment[],
      },
    };
  }, {});
});
